import { ShareableEntity } from "@/lib/api/shareable-entities";
import { useEffect, useRef } from "react";
import type { HubView } from "./use-hub-store";

function getViewForEntity(entity: ShareableEntity): HubView {
  if (entity.entityType === "activity") {
    const activityType = entity.activity?.activityType;
    if (activityType === "connect_moment") return "view-connect-moment";
    if (activityType === "hot_takes") return "view-hot-takes";
  }

  switch (entity.entityType) {
    case "moment":
      return "view-moment";
    case "swap":
      return "view-swap";
    case "note":
    default:
      return "view-note";
  }
}

interface UseHubEntityNavigationParams {
  entities: ShareableEntity[];
  selectedEntity: ShareableEntity | null;
  selectedNoteId: string | null;
  currentView: HubView;
  hasNextPage: boolean;
  fetchNextPage: () => Promise<unknown>;
  isFetchingNextPage: boolean;
  navigateToView: (
    view: HubView,
    entityId?: string | null,
    index?: number | null,
  ) => void;
}

const VIEWING_VIEWS: HubView[] = [
  "view-note",
  "view-moment",
  "view-swap",
  "view-connect-moment",
  "view-hot-takes",
];

export function useHubEntityNavigation({
  entities,
  selectedEntity,
  selectedNoteId,
  currentView,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
  navigateToView,
}: UseHubEntityNavigationParams) {
  const pendingNextNavigation = useRef<number | null>(null);

  useEffect(() => {
    if (
      VIEWING_VIEWS.includes(currentView) &&
      pendingNextNavigation.current !== null &&
      !isFetchingNextPage &&
      entities.length > pendingNextNavigation.current
    ) {
      const nextIndex = pendingNextNavigation.current;
      const nextEntity = entities[nextIndex];
      if (nextEntity) {
        navigateToView(getViewForEntity(nextEntity), nextEntity.id);
        pendingNextNavigation.current = null;
      }
    }
  }, [entities, isFetchingNextPage, currentView, navigateToView]);

  useEffect(() => {
    pendingNextNavigation.current = null;
  }, [selectedNoteId]);

  const onPrevious = () => {
    if (!selectedEntity) return;
    const currentIndex = entities.findIndex(
      (entity) => entity.id === selectedEntity.id,
    );
    if (currentIndex > 0) {
      const previousEntity = entities[currentIndex - 1];
      navigateToView(getViewForEntity(previousEntity), previousEntity.id);
    }
  };

  const onNext = async () => {
    if (!selectedEntity) return;
    const currentIndex = entities.findIndex(
      (entity) => entity.id === selectedEntity.id,
    );

    if (currentIndex === -1) {
      return;
    }

    if (currentIndex < entities.length - 1) {
      const nextEntity = entities[currentIndex + 1];
      navigateToView(getViewForEntity(nextEntity), nextEntity.id);
      return;
    }

    if (hasNextPage && !isFetchingNextPage) {
      try {
        pendingNextNavigation.current = currentIndex + 1;
        await fetchNextPage();
      } catch (error) {
        console.error("Failed to fetch next page:", error);
        pendingNextNavigation.current = null;
      }
    }
  };

  const onNavigateToList = () => {
    pendingNextNavigation.current = null;
    navigateToView("list");
  };

  return { onPrevious, onNext, onNavigateToList };
}
